/**
 * Soft≠Live Fleet Device Link & power — Live detail / diagnose / telemetry.
 * Soft “version 4”, Soft MQTT brochure, Soft kind→PoE invent stay deleted.
 */
import { useMemo, useState } from "react";
import { html, Ico, Card, KV, Alert } from "./ui.js";
import { path, useApi, useAction } from "./api.js";
import { envelopeSummary } from "./action-envelope.js";
import { presenceDiagnoseView } from "./fleet-live.js";
import { poeLiveFromTelemetry } from "./board-telemetry-adapter.js";

const EARTH_KM = 6371;

function haversineKm(a, b) {
  if (!a || !b || a.length < 2 || b.length < 2) return null;
  const [lng1, lat1] = a.map(Number);
  const [lng2, lat2] = b.map(Number);
  if (![lng1, lat1, lng2, lat2].every(Number.isFinite)) return null;
  if (Math.abs(lat1) > 90 || Math.abs(lat2) > 90) return null;
  const toR = (d) => (d * Math.PI) / 180;
  const dLat = toR(lat2 - lat1);
  const dLng = toR(lng2 - lng1);
  const x =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toR(lat1)) * Math.cos(toR(lat2)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(x)));
}

function homeOf(row) {
  return String((row && (row.site_id || row.site)) || "").trim();
}

/**
 * Nearest gateway homed on this board's site. Surveyed geometry only.
 * A closer mast on another site is a different place.
 */
export function nearestLiveMast(device, register) {
  const ll = device && Array.isArray(device.ll) ? device.ll : null;
  if (!ll) return { label: "—", km: null, site: "", honesty: "no surveyed position on this device" };
  const home = homeOf(device);
  if (!home) return { label: "—", km: null, site: "", honesty: "no home site on this device" };
  const gateways = (register || []).filter(
    (d) => d && d.id !== (device && device.id) && d.kind === "gateway"
      && Array.isArray(d.ll) && homeOf(d) === home,
  );
  if (!gateways.length) {
    return { label: "—", km: null, site: home, honesty: "no Live gateway homed on this site" };
  }
  let best = null;
  let bestKm = Infinity;
  for (const g of gateways) {
    const km = haversineKm(ll, g.ll);
    if (km == null) continue;
    if (km < bestKm) {
      bestKm = km;
      best = g;
    }
  }
  if (!best) {
    return { label: "—", km: null, site: home, honesty: "gateway positions unusable" };
  }
  return {
    label: best.label || best.id,
    km: bestKm,
    site: home,
    honesty: "straight-line to the nearest gateway homed on this site · not RF prediction",
  };
}

import { rssiOf, vbatOf } from "./device-overview-live.js";

function transportOf(detail, diagnose) {
  const go = detail && detail.gateway_observation && typeof detail.gateway_observation === "object"
    ? detail.gateway_observation : {};
  const named = String(go.transport || "").trim();
  if (named) return named + " · Live gateway_observation";
  const net = (detail && detail.net_health) || (diagnose && diagnose.net) || {};
  const hasMqtt =
    net.mqtt != null ||
    net.mqtt_reconnects != null ||
    go.mqtt_online === true ||
    String(go.transport || "").toLowerCase().includes("mqtt");
  if (hasMqtt) return "MQTT · Live net_health / observation";
  if (detail && detail.is_online) return "Online · transport not named in Live frame";
  return "—";
}

/**
 * @param {{ deviceId: string, device: object, register: object[], commandAllowed?: boolean, toast: function }} props
 */
export function DeviceLinkPowerLive({ deviceId, device, register, commandAllowed, toast }) {
  const did = String(deviceId || "").trim();
  const act = useAction();
  const [probeBusy, setProbeBusy] = useState(false);
  const cmdOk = commandAllowed === true;
  const detailLive = useApi(did ? path("get__devices_p", { pathParams: [did] }) : "");
  const diagLive = useApi(did ? path("get__devices_p_presence-diagnose", { pathParams: [did] }) : "");
  const teleLive = useApi(did ? path("get__devices_p_board-telemetry", { pathParams: [did] }) : "");

  const detail = detailLive.data && typeof detailLive.data === "object" ? detailLive.data : null;
  const diagnose = useMemo(() => presenceDiagnoseView(diagLive.data), [diagLive.data]);
  const poe = useMemo(() => poeLiveFromTelemetry(teleLive.data || null), [teleLive.data]);
  const mast = useMemo(
    () => nearestLiveMast(device || {}, register || []),
    [device, register],
  );

  const rssi = rssiOf(detail, device && device.rssi);
  const vbat = vbatOf(detail, device && device.vbat);
  const transport = transportOf(detail, diagnose);
  const cred =
    detail && detail.cred_version != null && Number(detail.cred_version) > 0
      ? ("version " + Number(detail.cred_version))
      : "—";
  const online = detail ? !!detail.is_online : !(device && device.offline);
  const loading = !!(detailLive.loading || diagLive.loading);

  const reload = () => {
    detailLive.reload && detailLive.reload();
    diagLive.reload && diagLive.reload();
    teleLive.reload && teleLive.reload();
    toast && toast("Link & power re-read · Live");
  };

  const requestTelemetry = async () => {
    if (!did) { toast("No device id", "warn"); return; }
    if (!cmdOk) { toast("Commands closed for this seat", "warn"); return; }
    setProbeBusy(true);
    try {
      const out = await act.run(
        path("post__devices_p_commands", { pathParams: [did] }),
        { method: "POST", body: { cmd: "ping", params: {} } },
      );
      if (!out) {
        toast(act.error || "Telemetry request refused — nothing was sent", "bad");
        return;
      }
      toast("Live ping queued · re-reading link envelope", "go");
      reload();
    } finally {
      setProbeBusy(false);
    }
  };

  const powerSource = (() => {
    if (poe && poe.fresh && poe.fields) return "PoE · Live board-telemetry";
    if (vbat != null) return "Battery voltage reported · source class not named";
    return "—";
  })();

  return html`<div className="cmc-link-live cmc-link-live--liquid" data-cmc-page="page99-device-live"
    data-cmc-nearest-mast=${mast.label} data-cmc-nearest-home=${mast.site || ""}>
    <${Alert} tone="info" title="Live link and power"
      say="Signal, credential version, transport and PoE come from Live GET /devices/{id}, presence-diagnose and board-telemetry. Soft “version 4”, Soft MQTT brochure and Soft kind→PoE invent stay deleted. Primary CTAs carry · Live."/>
    ${act.envelope
      ? html`<${KV} k="Last action" v=${envelopeSummary(act.envelope) || "—"} tone=${act.envelope.ok ? "hi" : "warn"}/>`
      : null}

    <div className="btn-row cmc-link-live__acts" style=${{ marginBottom:12, flexWrap:"wrap", gap:8 }}>
      <button className="btn btn--ghost btn--sm" type="button"
        disabled=${!did || loading}
        onClick=${reload}>
        <${Ico} d="refresh" w=${13}/>Re-read · Live</button>
      <button className="btn btn--ai btn--sm" type="button" data-cmc-cmd-ok=${cmdOk ? "1" : "0"}
        disabled=${!did || probeBusy || act.busy || !cmdOk}
        onClick=${requestTelemetry}>
        <${Ico} d="spark" w=${13}/>${probeBusy ? "Probing · Live…" : "Request Live · Live"}</button>
    </div>

    ${detailLive.error && !detail
      ? html`<${Alert} tone="warn" title="Live device detail unavailable"
          say=${String(detailLive.error)}/>`
      : null}

    <div className="grid g2">
      <${Card} title="Link" meta=${online ? "answered recently" : "not answering"}>
        <${KV} k="Presence" v=${online ? "online" : "offline"}
          tone=${online ? "go" : "bad"}/>
        <${KV} k="Signal"
          v=${rssi == null ? "—" : (rssi + " dBm")}
          tone=${rssi == null ? "dim" : (rssi < -72 ? "warn" : "")}/>
        <${KV} k="Transport" v=${transport} tone=${transport === "—" ? "dim" : ""}/>
        <${KV} k="Nearest mast"
          v=${mast.km == null
            ? mast.label
            : (mast.label + " · " + mast.km.toFixed(2) + " km")}
          tone=${mast.km == null ? "dim" : ""}/>
        <${KV} k="Credential version" v=${cred} tone=${cred === "—" ? "dim" : "go"}/>
        ${diagnose && diagnose.bucket
          ? html`<${KV} k="Presence bucket" v=${diagnose.bucket + (diagnose.suspect ? (" · " + diagnose.suspect) : "")}/>`
          : null}
        <p className="note">${mast.honesty}. Soft fixed “version 4” and Soft MQTT brochure stay deleted.</p>
        ${diagnose && diagnose.net && (diagnose.net.wifi != null || diagnose.net.mqtt != null)
          ? html`<div className="grid g2" style=${{ gap:"0 12px", marginTop:8 }}>
              <${KV} k="Wi-Fi rejoins" v=${diagnose.net.wifi == null ? "—" : String(diagnose.net.wifi)}/>
              <${KV} k="MQTT rejoins" v=${diagnose.net.mqtt == null ? "—" : String(diagnose.net.mqtt)}/>
              <${KV} k="MQTT fail streak" v=${diagnose.net.failStreak == null ? "—" : String(diagnose.net.failStreak)}/>
            </div>`
          : html`<p className="note">net_health counters empty until the device reports them.</p>`}
      </${Card}>

      <${Card} title="Power" meta=${poe && poe.fresh ? "Live PoE / battery" : "reported when present"}>
        <${KV} k="Battery"
          v=${vbat == null ? "—" : (vbat + " V")}
          tone=${vbat == null ? "dim" : (vbat < 3.8 ? "warn" : "")}/>
        <${KV} k="Source" v=${powerSource} tone=${powerSource === "—" ? "dim" : ""}/>
        <${KV} k="PoE supply"
          v=${poe && poe.fields && poe.fields.watts != null ? (poe.fields.watts + " W") : "—"}
          tone=${poe && poe.fields && poe.fields.watts != null ? "go" : "dim"}/>
        <${KV} k="IEEE class"
          v=${poe && poe.fields && poe.fields.classN != null ? String(poe.fields.classN) : "—"}
          tone="dim"/>
        <p className="note">Soft kind→PoE invent is deleted. Empty means the Live frame has no power class yet. Volts are not turned into a fake percentage.</p>
      </${Card}>
    </div>
  </div>`;
}
